import React from 'react';
import {
    // Composants communs
    Title,
    Button,
    ButtonsContainer,

    // Composants pour la sélection de tâches
    TaskSelectionContainer,
    WelcomeSection,
    WelcomeTitle,
    WelcomeDescription,
    TaskTypeContainerTitle,
    TaskTypeContainer,
    TaskTypeCard,
    TaskTypeIcon,
    TaskTypeTitle,
    TaskTypeDescription,
    TaskTypeDetails,
    TaskTypeTag,
    StartButtonContainer,
    StartButton,

    // Composants pour l'écran d'examen
    Header,
    TimerContainer,
    TabsContainer,
    Tab,
    TaskContainer,
    TaskHeader,
    TaskTitle,
    InstructionContainer,
    InstructionToggle,
    EditorContainer,
    Editor,
    EditorFooter,
    WordCount,

    // Composants pour les modales
    ConfirmationModal as ConfirmationModalContainer,
    ModalContent,
    ModalTitle,
    ModalButtons,

    // Composant de complétion
    CompletionMessage
} from './TCFWritingStyles';

import {
    taskDefinitions,
    formatTime,
    getWordCountStatus,
    canCompleteExam
} from '../services/utils';
import TextWithLineBreaks from './Utils/TextWithLineBreaks';


/**
 * Écran de sélection des tâches
 */
export const TaskSelectionScreen = ({ selectedExamType, setSelectedExamType, startExam }) => {
    return (
        <TaskSelectionContainer>
            <WelcomeSection>
                <WelcomeTitle>TCF Canada - Épreuve d'expression écrite</WelcomeTitle>
                <WelcomeDescription>
                    Bienvenue dans le module de simulation de l'épreuve d'expression écrite du TCF Canada.
                    Cet exercice vous permettra de vous entraîner aux différentes tâches d'écriture et de gérer votre temps comme lors du véritable examen.
                </WelcomeDescription>
            </WelcomeSection>

            <TaskTypeContainerTitle>Choisissez votre exercice</TaskTypeContainerTitle>

            <TaskTypeContainer>
                <TaskTypeCard
                    selected={selectedExamType === 'complete'}
                    onClick={() => setSelectedExamType('complete')}
                >
                    <TaskTypeIcon>📋</TaskTypeIcon>
                    <TaskTypeTitle>Examen complet</TaskTypeTitle>
                    <TaskTypeDescription>
                        Simulez l'examen complet avec les trois tâches d'expression écrite, comme dans les conditions réelles du TCF Canada.
                    </TaskTypeDescription>
                    <TaskTypeDetails>
                        <TaskTypeTag>
                            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                            60 minutes
                        </TaskTypeTag>
                        <TaskTypeTag>3 tâches</TaskTypeTag>
                    </TaskTypeDetails>
                </TaskTypeCard>

                {/* Cartes pour chaque tâche individuelle */}
                {Object.keys(taskDefinitions).map(taskKey => (
                    <TaskTypeCard
                        key={taskKey}
                        selected={selectedExamType === taskKey}
                        onClick={() => setSelectedExamType(taskKey)}
                    >
                        <TaskTypeIcon>{taskDefinitions[taskKey].icon}</TaskTypeIcon>
                        <TaskTypeTitle>{taskDefinitions[taskKey].title}</TaskTypeTitle>
                        <TaskTypeDescription>
                            {taskDefinitions[taskKey].instructions}
                        </TaskTypeDescription>
                        <TaskTypeDetails>
                            <TaskTypeTag>
                                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                                {taskDefinitions[taskKey].duration} minutes
                            </TaskTypeTag>
                            <TaskTypeTag>{taskDefinitions[taskKey].expectedWords}</TaskTypeTag>
                        </TaskTypeDetails>
                    </TaskTypeCard>
                ))}
            </TaskTypeContainer>

            <StartButtonContainer>
                <StartButton
                    primary
                    disabled={!selectedExamType}
                    onClick={startExam}
                >
                    Commencer l'examen
                </StartButton>
            </StartButtonContainer>
        </TaskSelectionContainer>
    );
};

/**
 * Écran d'examen
 */
export const ExamScreen = ({
    selectedExamType,
    currentTask,
    switchTask,
    timeLeft,
    responses,
    updateResponse,
    instructionsCollapsed,
    toggleInstructions,
    showConfirmation,
    countWords,
    subjects
}) => {
    const isTimeAlmostUp = timeLeft <= 300; // 5 minutes ou moins
    const wordCount = countWords(responses[currentTask]);
    const wordCountStatus = getWordCountStatus(currentTask, wordCount);
    const isCompletionEnabled = canCompleteExam(selectedExamType, responses);

    // Trouver le sujet correspondant à la tâche actuelle
    const getTaskSubject = () => {
        if (!subjects || subjects.length === 0) return null;

        // Mapping des tâches vers les numéros de question
        const taskToQuestionNumber = {
            'task1': 1,
            'task2': 2,
            'task3': 3
        };

        const questionNumber = taskToQuestionNumber[currentTask];
        return subjects.find(subject =>
            subject.criteria.questionNumber === questionNumber &&
            subject.success &&
            subject.data
        );
    };

    const currentSubject = getTaskSubject();
    const consigne = currentSubject?.data?.consigne || '';

    // Gérer les retours à la ligne dans les réponses
    const handleEditorChange = (e) => {
        // On conserve le texte tel quel avec les retours à la ligne
        updateResponse(currentTask, e.target.value);
    };

    return (
        <>
            <Header>
                <Title>TCF Canada - Expression écrite</Title>
                <TimerContainer isLow={isTimeAlmostUp}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    Temps restant: {formatTime(timeLeft)}
                </TimerContainer>
            </Header>

            {selectedExamType === 'complete' && (
                <TabsContainer>
                    {Object.keys(taskDefinitions).map(taskKey => (
                        <Tab
                            key={taskKey}
                            active={currentTask === taskKey}
                            onClick={() => switchTask(taskKey)}
                        >
                            {taskDefinitions[taskKey].title}
                        </Tab>
                    ))}
                </TabsContainer>
            )}

            <TaskContainer>
                <TaskHeader>
                    <TaskTitle>{taskDefinitions[currentTask].title}</TaskTitle>
                    <span>{taskDefinitions[currentTask].expectedWords} attendus</span>
                </TaskHeader>

                <InstructionContainer collapsed={instructionsCollapsed[currentTask]}>
                    {/* Utilisation de la consigne du sujet au lieu des instructions par défaut */}
                    <div> <TextWithLineBreaks text={consigne} /></div>
                    {instructionsCollapsed[currentTask] && '...'}
                </InstructionContainer>

                <InstructionToggle onClick={() => toggleInstructions(currentTask)}>
                    {instructionsCollapsed[currentTask] ? (
                        <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                            Afficher les instructions
                        </>
                    ) : (
                        <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="18 15 12 9 6 15"></polyline>
                            </svg>
                            Masquer les instructions
                        </>
                    )}
                </InstructionToggle>

                <EditorContainer>
                    <Editor
                        value={responses[currentTask]}
                        onChange={handleEditorChange}
                        placeholder="Rédigez votre réponse ici..."
                    />

                    <EditorFooter>
                        <WordCount warning={wordCountStatus === 'warning'} danger={wordCountStatus === 'danger'}>
                            {wordCount} {wordCount <= 1 ? 'mot' : 'mots'}
                        </WordCount>

                        <ButtonsContainer>
                            {selectedExamType === 'complete' && currentTask !== 'task3' && (
                                <Button onClick={() => switchTask(currentTask === 'task1' ? 'task2' : 'task3')}>
                                    Tâche suivante
                                </Button>
                            )}
                            <Button
                                primary
                                disabled={!isCompletionEnabled}
                                onClick={() => showConfirmation('complete')}
                            >
                                Terminer l'examen
                            </Button>
                        </ButtonsContainer>
                    </EditorFooter>
                </EditorContainer>
            </TaskContainer>
        </>
    );
};

/**
 * Écran de complétion de l'examen
 */
export const CompletedScreen = ({ resetModule, evaluationResults }) => {
    return (
        <CompletionMessage>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#2ecc71" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <h2>Examen terminé</h2>
            <p>Vos réponses ont été enregistrées avec succès.</p>

            {evaluationResults && (
                <div className="evaluation-results">
                    <h3>Résultats de l'évaluation</h3>
                    <div className="results-container">
                        {evaluationResults.results.map((result, index) => (
                            <div key={index} className="result-card">
                                <h4>Tâche {result.subject_type}</h4>
                                <div className="score-container">
                                    <div className="score">
                                        <strong>Score final:</strong> {result.global_evaluation.final_score}/20
                                    </div>
                                    <div className="word-count">
                                        <strong>Nombre de mots:</strong> {result.global_evaluation.word_count}
                                    </div>
                                </div>

                                <div className="strengths">
                                    <h5>Points forts:</h5>
                                    <ul>
                                        {result.global_evaluation.strengths.map((strength, i) => (
                                            <li key={i}>{strength}</li>
                                        ))}
                                    </ul>
                                </div>

                                {result.global_evaluation.improvement_areas.length > 0 && (
                                    <div className="improvement-areas">
                                        <h5>Axes d'amélioration:</h5>
                                        <ul>
                                            {result.global_evaluation.improvement_areas.map((area, i) => (
                                                <li key={i}>{area}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <ButtonsContainer style={{ justifyContent: 'center', marginTop: '24px' }}>
                <Button primary onClick={resetModule}>
                    Revenir à l'accueil
                </Button>
            </ButtonsContainer>
        </CompletionMessage>
    );
};


/**
 * Modal de confirmation
 */
export const ConfirmationModal = ({ onCancel, onConfirm }) => {
    return (
        <ConfirmationModalContainer>
            <ModalContent>
                <ModalTitle>Confirmer la fin de l'examen</ModalTitle>
                <p>Êtes-vous sûr de vouloir terminer l'examen ? Cette action est irréversible.</p>

                <ModalButtons>
                    <Button onClick={onCancel}>
                        Annuler
                    </Button>
                    <Button primary onClick={onConfirm}>
                        Terminer l'examen
                    </Button>
                </ModalButtons>
            </ModalContent>
        </ConfirmationModalContainer>
    );
};